// Bilingual content for Hacienda Carey — single source of truth
export const translations = {
  en: {
    meta: {
      title: "Hacienda Carey — Authentic Puerto Rican Adventures",
      description:
        "Family-run, eco-conscious adventures in Aguadilla & Isabela. Horseback rides on the beach, snorkeling, and farm experiences for kids.",
    },
    gate: {
      eyebrow: "Hacienda Carey",
      welcome: "Welcome",
      welcomeAlt: "Bienvenidos",
      sub: "Pick your language to begin the journey.",
      english: "Continue in English",
      spanish: "Continuar en Español",
      hint: "You can switch language anytime from the header.",
    },
    nav: {
      activities: "Activities",
      about: "Our Story",
      reviews: "Reviews",
      blog: "Blog",
      partners: "Partners",
      contact: "Contact",
      book: "Book Now",
    },
    hero: {
      eyebrow: "Aguadilla · Isabela · Puerto Rico",
      title_pre: "Authentic",
      title_em: "Puerto Rican",
      title_post: "Adventures.",
      sub: "Family-run, eco-conscious experiences where the mountains meet the sea. Ride along sunset shores, snorkel coral reefs, and reconnect with the wild side of the island.",
      cta_primary: "Book Your Adventure",
      cta_secondary: "Explore Activities",
      stat_rating: "Google Rating",
      stat_reviews: "Happy Guests",
      stat_years: "Since 2019",
    },
    highlights: {
      title: "The Hacienda Carey way",
      items: [
        {
          title: "Top-Rated",
          body: "4.7★ on Google with nearly 100 glowing reviews from travelers worldwide.",
        },
        {
          title: "Local Guides",
          body: "Ricky, Freddy & Armando — born-and-raised guides who know every wave and trail.",
        },
        {
          title: "Eco-Conscious",
          body: "We protect what we love. Small groups, well-cared-for horses, leave-no-trace tours.",
        },
        {
          title: "Family Friendly",
          body: "Adventures designed for first-time riders, kids, and grandparents alike.",
        },
      ],
    },
    activities: {
      eyebrow: "Our Adventures",
      title: "Choose your day.",
      sub: "From sunset beach rides to coral reefs and farm visits — every tour is led by a real Puerto Rican guide.",
      book: "Book Now",
      learn_more: "Learn More",
      coming_soon: "Coming Soon",
      waitlist: "Join Waitlist",
      list: [
        {
          id: "horseback-family",
          name: "Beginner & Family Horseback Ride",
          place: "Hacienda Carey Ranch, Isabela",
          duration: "1.5 hours",
          price: "$60",
          tagline: "New — inland ride",
          description:
            "Gentle guided ride through the green inland hills of Isabela. No experience needed — small groups, shaded trails.",
          href: "/horseback-family",
          internal: true,
          status: "live",
        },
        {
          id: "horseback-beginner",
          name: "Isabela Beach Horseback Ride",
          place: "Isabela Beach, Isabela",
          duration: "1.5 hours",
          price: "$100",
          tagline: "Most popular",
          description:
            "Our signature ride. Calm horses, soft sand, and the Caribbean breeze — perfect for first-time riders.",
          href: "/horseback-beginner",
          internal: true,
          status: "live",
        },
        {
          id: "horseback-sunset",
          name: "Isabela Sunset Beach Horseback Ride",
          place: "Isabela Beach, Isabela",
          duration: "2 hours",
          price: "$125",
          tagline: "Golden hour",
          description:
            "Ride the shoreline as the sun melts into the ocean. Includes professional photos by your guide.",
          href: "/horseback-sunset",
          internal: true,
          status: "live",
        },
        {
          id: "snorkel-black-eagle",
          name: "Snorkel Adventure",
          place: "Black Eagle Beach",
          duration: "2 hours",
          price: "From $75",
          tagline: "Hidden reef",
          description:
            "Swim alongside parrotfish, sea turtles and elkhorn coral in one of Aguadilla's clearest coves.",
          href: "/snorkel-black-eagle",
          internal: true,
          status: "live",
        },
        {
          id: "kids-farm",
          name: "Agro-Education Kids Farm Program",
          place: "Hacienda Carey, Aguadilla",
          duration: "3 hours",
          price: "$40",
          tagline: "All ages",
          description:
            "A hands-on, screen-free farm session: baby goats, guided nature hike, bilingual learning and a healthy snack.",
          href: "/kids-farm",
          internal: true,
          status: "live",
        },
        {
          id: "scuba",
          name: "Scuba Diving Adventure",
          place: "West Coast Reefs",
          duration: "Half day",
          price: "TBA",
          tagline: "Launching 2026",
          description:
            "PADI-certified dives along Puerto Rico's wild west coast. Be the first to know when we launch.",
          href: "#contact",
          status: "coming-soon",
        },
      ],
    },
    about: {
      eyebrow: "Our Story",
      title: "Where the mountain meets the sea.",
      body_1:
        "Hacienda Carey was born from a simple idea: that the best way to fall in love with Puerto Rico is to feel it under your feet — and your horse's hooves. Our family has guided travelers along these shores for years, sharing the island we grew up on.",
      body_2:
        "Every tour is eco-conscious by design. We keep groups small, our horses are loved and well-rested, and our guides Ricky, Freddy and Armando treat every guest like family. Whether it's your first time on a horse or your hundredth dive, you'll leave with stories that last.",
      cta: "Meet our guides",
      stat_1_value: "97+",
      stat_1_label: "Five-star moments",
      stat_2_value: "6",
      stat_2_label: "Years on the coast",
      stat_3_value: "100%",
      stat_3_label: "Local-owned",
    },
    reviews: {
      eyebrow: "Real Travelers",
      title: "Words from our guests.",
      sub: "4.7★ average across 97+ Google reviews.",
      cta: "Review us on Google",
      list: [
        {
          name: "Kimri Swanson",
          when: "3 months ago",
          quote:
            "Freddy was fantastic — friendly, patient, and clearly experienced. Riding along the shoreline at sunset was unforgettable.",
        },
        {
          name: "Olga",
          when: "5 months ago",
          quote:
            "My first time horseback riding and it was awesome! Ricky kept us entertained the whole time and takes amazing photos — you don't even need your phone.",
        },
        {
          name: "Eden Ellis",
          when: "5 months ago",
          quote:
            "Calm and well-trained horses, beautiful views, and very friendly staff. One of the highlights of our trip. Highly recommend!",
        },
        {
          name: "jmatteo17",
          when: "4 months ago",
          quote:
            "Armando was a reassuring guide and made sure my 77-yr old godmother was comfortable and safe. The path goes on the beach and through the woods.",
        },
        {
          name: "Beatriz Bueno",
          when: "8 months ago",
          quote:
            "Ricky was absolutely amazing and funny. The tour was so much fun and he takes amazing post-worthy pictures. 10/10 will do again next visit!",
        },
      ],
    },
    cta_band: {
      title: "Ready to ride?",
      sub: "Reserve your spot in minutes through our secure FareHarbor booking system.",
      button: "Book an Adventure",
    },
    newsletter: {
      eyebrow: "Stay in the loop",
      title: "New tours. Sunset shoots. Local secrets.",
      sub: "Drop your email. We'll send a few good things a month — never spam.",
      placeholder: "your@email.com",
      button: "Subscribe",
      success: "You're in! Check your inbox.",
      error: "Please enter a valid email.",
    },
    footer: {
      tagline: "Authentic Puerto Rican Adventures since 2019.",
      explore: "Explore",
      adventures: "Adventures",
      contact: "Contact",
      follow: "Follow",
      based: "Based in Aguadilla & Isabela, Puerto Rico",
      rights: "All rights reserved.",
    },
    kids_farm: {
      eyebrow: "Agro-Education Kids Program",
      title_pre: "A hands-on",
      title_em: "farm adventure",
      title_post: "for kids of all ages.",
      sub: "A 3-hour screen-free experience at Hacienda Carey in Aguadilla, Puerto Rico. Animal petting, guided nature hikes, bilingual learning and a healthy snack — designed to spark curiosity and joy.",
      cta_book: "Book a Session",
      cta_secondary: "See what's included",
      stats: {
        price: { value: "$40", label: "Per child · drop-in" },
        duration: { value: "3 hours", label: "Per session" },
        ages: { value: "All ages", label: "Bilingual EN/ES" },
        electronics: { value: "Screen-free", label: "No phones or tablets" },
      },
      sessions_title: "Two sessions, weekdays only",
      sessions_sub:
        "Drop your kids in for the morning before camp, or afternoon after school. No package required — just pick a date.",
      sessions: [
        {
          tag: "Morning",
          time: "8:00 AM – 11:00 AM",
          days: "Monday – Friday",
        },
        {
          tag: "Afternoon",
          time: "3:30 PM – 6:30 PM",
          days: "Monday – Friday",
        },
      ],
      included_title: "What's included",
      included_list: [
        "3-hour guided farm session",
        "Animal petting with goats, pigs and sheep",
        "Guided nature hike around Hacienda Carey",
        "Weekly rotating educational guide (bilingual EN/ES)",
        "Lite healthy snack provided by Hacienda Carey",
        "Bilingual staff supervision throughout",
        "Small take-home keepsake or activity sheet",
        "Free parking on-site",
      ],
      not_included_title: "Not included",
      not_included_list: [
        "Electronic devices (strictly not permitted during the session)",
        "Horseback riding (bookable separately)",
        "Lunch or full meals",
      ],
      bring_title: "What to bring",
      bring_list: [
        "Closed-toe shoes — required (no sandals or flip flops)",
        "Comfortable clothes that can get dirty",
        "Sunscreen and a hat",
        "Reusable water bottle",
        "No phones, tablets or electronic devices",
      ],
      schedule_title: "Your 3-hour at the farm",
      schedule_sub:
        "Same flow for morning and afternoon sessions. Every block is supervised by our bilingual team.",
      schedule: [
        {
          time: "0:00 – 0:10",
          title: "Welcome",
          body: "Greet kids, introduce staff, review farm rules and the no-electronics policy. Hand out name tags.",
        },
        {
          time: "0:10 – 0:45",
          title: "Animal Petting",
          body: "Hands-on time with baby goats, pigs and sheep. Learn names, behaviors and safe handling.",
        },
        {
          time: "0:45 – 1:15",
          title: "Nature Hike",
          body: "Guided walk around the Hacienda property. Identify native plants, trees and birds.",
        },
        {
          time: "1:15 – 1:35",
          title: "Lite Snack Time",
          body: "Healthy farm-inspired snack. Rest, hydrate and chat about what they discovered so far.",
        },
        {
          time: "1:35 – 2:30",
          title: "Educational Guide",
          body: "Structured bilingual learning. Topics rotate weekly: farm life, animal science, plant biology, nutrition.",
        },
        {
          time: "2:30 – 2:50",
          title: "Free Farm Exploration",
          body: "Open time to revisit favorite animals, explore the petting zoo or walk the garden.",
        },
        {
          time: "2:50 – 3:00",
          title: "Wrap-Up Circle",
          body: "Each child shares one thing they learned. Kids take home a small keepsake or activity sheet.",
        },
      ],
      pricing_title: "Simple, drop-in pricing",
      pricing_sub: "One session, one flat price. For schools or large groups, get in touch directly.",
      pricing: [
        {
          name: "General Admission",
          price: "$40",
          unit: "/ child / session",
          desc: "Drop-in per session, morning or afternoon. No package required.",
          featured: true,
        },
        {
          name: "Schools & Big Groups",
          price: "Let's talk",
          unit: "Email or call us",
          desc: "Schools, camps and groups of 8+ — contact us directly for custom pricing and dates.",
          contact: true,
        },
      ],
      faq_title: "Good to know",
      faq: [
        {
          q: "Is there a minimum age?",
          a: "No. The program is built for all ages — toddlers with a parent, school-age kids, and even older teens are all welcome.",
        },
        {
          q: "Do parents need to stay?",
          a: "Drop-off is welcome. Parents are also welcome to stay and join the experience.",
        },
        {
          q: "Why no electronics?",
          a: "We protect the magic of an unplugged afternoon — kids reconnect with animals, nature and each other. Phones and tablets are held at check-in.",
        },
        {
          q: "Is it bilingual?",
          a: "Yes. Our entire team runs the program in both English and Spanish.",
        },
        {
          q: "Can we add horseback riding?",
          a: "Yes — ask us about combining the farm session with our beach horseback rides for the full Hacienda Carey day.",
        },
      ],
      cta_band: {
        title: "Reserve your spot at the farm.",
        sub: "Sessions fill fast on weekdays. Pick a morning or afternoon that works for the family and book in under a minute.",
        button: "Book a Session",
      },
    },
  },

  es: {
    meta: {
      title: "Hacienda Carey — Aventuras Auténticas de Puerto Rico",
      description:
        "Aventuras eco-conscientes de familia en Aguadilla e Isabela. Cabalgatas en la playa, snorkel y experiencias de finca para niños.",
    },
    gate: {
      eyebrow: "Hacienda Carey",
      welcome: "Bienvenidos",
      welcomeAlt: "Welcome",
      sub: "Elige tu idioma para comenzar la aventura.",
      english: "Continue in English",
      spanish: "Continuar en Español",
      hint: "Puedes cambiar el idioma en cualquier momento desde el menú.",
    },
    nav: {
      activities: "Actividades",
      about: "Nuestra Historia",
      reviews: "Opiniones",
      blog: "Blog",
      partners: "Aliados",
      contact: "Contacto",
      book: "Reservar",
    },
    hero: {
      eyebrow: "Aguadilla · Isabela · Puerto Rico",
      title_pre: "Aventuras",
      title_em: "Auténticas",
      title_post: "de Puerto Rico.",
      sub: "Experiencias familiares y eco-conscientes donde la montaña se encuentra con el mar. Cabalga al atardecer, haz snorkel entre arrecifes y reconecta con el lado salvaje de la isla.",
      cta_primary: "Reserva tu Aventura",
      cta_secondary: "Ver Actividades",
      stat_rating: "Calificación Google",
      stat_reviews: "Huéspedes felices",
      stat_years: "Desde 2019",
    },
    highlights: {
      title: "El estilo Hacienda Carey",
      items: [
        {
          title: "Los mejor valorados",
          body: "4.7★ en Google con casi 100 reseñas brillantes de viajeros de todo el mundo.",
        },
        {
          title: "Guías locales",
          body: "Ricky, Freddy y Armando — guías nacidos y criados aquí que conocen cada ola y sendero.",
        },
        {
          title: "Eco-consciente",
          body: "Protegemos lo que amamos. Grupos pequeños, caballos bien cuidados, tours sin huella.",
        },
        {
          title: "Para toda la familia",
          body: "Aventuras diseñadas para principiantes, niños y abuelos por igual.",
        },
      ],
    },
    activities: {
      eyebrow: "Nuestras Aventuras",
      title: "Elige tu día.",
      sub: "Desde cabalgatas al atardecer hasta arrecifes de coral y visitas a la finca — cada tour lo guía un puertorriqueño de verdad.",
      book: "Reservar",
      learn_more: "Más Información",
      coming_soon: "Próximamente",
      waitlist: "Lista de espera",
      list: [
        {
          id: "horseback-family",
          name: "Cabalgata para Principiantes y Familias",
          place: "Hacienda Carey Ranch, Isabela",
          duration: "1.5 horas",
          price: "$60",
          tagline: "Nuevo — paseo interior",
          description:
            "Paseo guiado y tranquilo por las lomas verdes del interior de Isabela. Sin experiencia — grupos pequeños, veredas con sombra.",
          href: "/horseback-family",
          internal: true,
          status: "live",
        },
        {
          id: "horseback-beginner",
          name: "Cabalgata en la Playa de Isabela",
          place: "Playa de Isabela, Isabela",
          duration: "1.5 horas",
          price: "$100",
          tagline: "Más popular",
          description:
            "Nuestra cabalgata estrella. Caballos tranquilos, arena suave y la brisa del Caribe — perfecta para principiantes.",
          href: "/horseback-beginner",
          internal: true,
          status: "live",
        },
        {
          id: "horseback-sunset",
          name: "Cabalgata al Atardecer en la Playa de Isabela",
          place: "Playa de Isabela, Isabela",
          duration: "2 horas",
          price: "$125",
          tagline: "Hora dorada",
          description:
            "Cabalga por la orilla mientras el sol se derrite en el océano. Incluye fotos profesionales del guía.",
          href: "/horseback-sunset",
          internal: true,
          status: "live",
        },
        {
          id: "snorkel-black-eagle",
          name: "Aventura de Snorkel",
          place: "Playa Black Eagle",
          duration: "2 horas",
          price: "Desde $75",
          tagline: "Arrecife escondido",
          description:
            "Nada con peces loro, tortugas marinas y corales cuerno de alce en una de las calas más claras de Aguadilla.",
          href: "/snorkel-black-eagle",
          internal: true,
          status: "live",
        },
        {
          id: "kids-farm",
          name: "Programa Agro-Educativo para Niños",
          place: "Hacienda Carey, Aguadilla",
          duration: "3 horas",
          price: "$40",
          tagline: "Todas las edades",
          description:
            "Sesión práctica sin pantallas: cabritos, caminata guiada, aprendizaje bilingüe y merienda saludable.",
          href: "/kids-farm",
          internal: true,
          status: "live",
        },
        {
          id: "scuba",
          name: "Aventura de Buceo",
          place: "Arrecifes Costa Oeste",
          duration: "Medio día",
          price: "Por anunciar",
          tagline: "Llega en 2026",
          description:
            "Inmersiones con certificación PADI en la salvaje costa oeste. Sé el primero en enterarte cuando lancemos.",
          href: "#contact",
          status: "coming-soon",
        },
      ],
    },
    about: {
      eyebrow: "Nuestra Historia",
      title: "Donde la montaña se encuentra con el mar.",
      body_1:
        "Hacienda Carey nació de una idea simple: que la mejor forma de enamorarse de Puerto Rico es sentirlo bajo tus pies — y bajo las pezuñas de tu caballo. Nuestra familia lleva años guiando viajeros por estas costas, compartiendo la isla donde crecimos.",
      body_2:
        "Cada tour es eco-consciente por diseño. Mantenemos grupos pequeños, nuestros caballos son amados y descansados, y nuestros guías Ricky, Freddy y Armando tratan a cada huésped como familia. Sea tu primera cabalgata o tu inmersión número cien, te irás con historias que perduran.",
      cta: "Conoce a nuestros guías",
      stat_1_value: "97+",
      stat_1_label: "Momentos cinco estrellas",
      stat_2_value: "6",
      stat_2_label: "Años en la costa",
      stat_3_value: "100%",
      stat_3_label: "Negocio local",
    },
    reviews: {
      eyebrow: "Viajeros Reales",
      title: "Lo que dicen nuestros huéspedes.",
      sub: "4.7★ promedio en más de 97 reseñas de Google.",
      cta: "Déjanos tu opinión en Google",
      list: [
        {
          name: "Kimri Swanson",
          when: "Hace 3 meses",
          quote:
            "Freddy fue fantástico — amable, paciente y con mucha experiencia. Cabalgar por la orilla al atardecer fue inolvidable.",
        },
        {
          name: "Olga",
          when: "Hace 5 meses",
          quote:
            "¡Mi primera vez montando a caballo y fue increíble! Ricky nos mantuvo entretenidos todo el tiempo y toma fotos increíbles — ni siquiera necesitas tu teléfono.",
        },
        {
          name: "Eden Ellis",
          when: "Hace 5 meses",
          quote:
            "Caballos tranquilos y bien entrenados, vistas hermosas y un equipo muy amable. Uno de los puntos altos de nuestro viaje. ¡Muy recomendado!",
        },
        {
          name: "jmatteo17",
          when: "Hace 4 meses",
          quote:
            "Armando fue un guía tranquilizador y se aseguró de que mi madrina de 77 años estuviera cómoda y segura. El camino va por la playa y por el bosque.",
        },
        {
          name: "Beatriz Bueno",
          when: "Hace 8 meses",
          quote:
            "Ricky fue absolutamente increíble y muy chistoso. El tour fue divertidísimo y toma fotos dignas de redes. 10/10 lo repetiré en mi próxima visita.",
        },
      ],
    },
    cta_band: {
      title: "¿Listo para cabalgar?",
      sub: "Reserva tu espacio en minutos a través de nuestro sistema seguro FareHarbor.",
      button: "Reservar una Aventura",
    },
    newsletter: {
      eyebrow: "Mantente al día",
      title: "Nuevos tours. Sesiones al atardecer. Secretos locales.",
      sub: "Déjanos tu correo. Enviamos pocas cosas buenas al mes — nunca spam.",
      placeholder: "tu@email.com",
      button: "Suscribirme",
      success: "¡Listo! Revisa tu bandeja.",
      error: "Por favor ingresa un correo válido.",
    },
    footer: {
      tagline: "Aventuras auténticas de Puerto Rico desde 2019.",
      explore: "Explorar",
      adventures: "Aventuras",
      contact: "Contacto",
      follow: "Síguenos",
      based: "Basados en Aguadilla e Isabela, Puerto Rico",
      rights: "Todos los derechos reservados.",
    },
    kids_farm: {
      eyebrow: "Programa Agro-Educativo",
      title_pre: "Una aventura",
      title_em: "práctica en la finca",
      title_post: "para niños de todas las edades.",
      sub: "Una experiencia de 3 horas sin pantallas en Hacienda Carey, Aguadilla. Animales, caminata guiada por la naturaleza, aprendizaje bilingüe y una merienda saludable — diseñada para despertar curiosidad y alegría.",
      cta_book: "Reservar una Sesión",
      cta_secondary: "Ver qué incluye",
      stats: {
        price: { value: "$40", label: "Por niño · drop-in" },
        duration: { value: "3 horas", label: "Por sesión" },
        ages: { value: "Todas las edades", label: "Bilingüe EN/ES" },
        electronics: { value: "Sin pantallas", label: "Sin celulares ni tablets" },
      },
      sessions_title: "Dos sesiones, días de semana",
      sessions_sub:
        "Déjalos en la mañana antes del campamento o en la tarde después de la escuela. Sin paquetes — solo elige un día.",
      sessions: [
        {
          tag: "Mañana",
          time: "8:00 AM – 11:00 AM",
          days: "Lunes – Viernes",
        },
        {
          tag: "Tarde",
          time: "3:30 PM – 6:30 PM",
          days: "Lunes – Viernes",
        },
      ],
      included_title: "Qué incluye",
      included_list: [
        "Sesión guiada de 3 horas en la finca",
        "Contacto con cabras, cerdos y ovejas",
        "Caminata guiada por Hacienda Carey",
        "Guía educativa semanal rotativa (bilingüe EN/ES)",
        "Merienda saludable provista por Hacienda Carey",
        "Supervisión bilingüe durante toda la sesión",
        "Pequeño recuerdo u hoja de actividades para llevar",
        "Estacionamiento gratis en el lugar",
      ],
      not_included_title: "No incluye",
      not_included_list: [
        "Dispositivos electrónicos (no permitidos durante la sesión)",
        "Cabalgatas (reservables por separado)",
        "Almuerzo o comidas completas",
      ],
      bring_title: "Qué traer",
      bring_list: [
        "Zapatos cerrados — obligatorios (sin sandalias ni chancletas)",
        "Ropa cómoda que se pueda ensuciar",
        "Protector solar y sombrero",
        "Botella reusable de agua",
        "Sin celulares, tablets ni dispositivos electrónicos",
      ],
      schedule_title: "Tus 3 horas en la finca",
      schedule_sub:
        "El mismo flujo para la sesión de mañana y la de tarde. Cada bloque supervisado por nuestro equipo bilingüe.",
      schedule: [
        {
          time: "0:00 – 0:10",
          title: "Bienvenida",
          body: "Saludamos, presentamos al equipo, repasamos las reglas y la política de no electrónicos. Repartimos nombres.",
        },
        {
          time: "0:10 – 0:45",
          title: "Contacto con animales",
          body: "Tiempo práctico con cabras, cerdos y ovejas. Aprenden nombres, comportamientos y manejo seguro.",
        },
        {
          time: "0:45 – 1:15",
          title: "Caminata por la naturaleza",
          body: "Recorrido guiado por la Hacienda. Identifican plantas nativas, árboles y aves.",
        },
        {
          time: "1:15 – 1:35",
          title: "Hora de merienda",
          body: "Merienda saludable inspirada en la finca. Descansan, se hidratan y comentan lo descubierto.",
        },
        {
          time: "1:35 – 2:30",
          title: "Guía educativa",
          body: "Aprendizaje estructurado y bilingüe. Temas rotativos: vida en la finca, animales, plantas, nutrición.",
        },
        {
          time: "2:30 – 2:50",
          title: "Exploración libre",
          body: "Tiempo libre para volver con sus animales favoritos, explorar el petting zoo o caminar el jardín.",
        },
        {
          time: "2:50 – 3:00",
          title: "Círculo de cierre",
          body: "Cada niño comparte algo que aprendió. Se llevan un recuerdo o una hoja de actividades.",
        },
      ],
      pricing_title: "Precio simple, drop-in",
      pricing_sub: "Una sesión, un precio fijo. Para escuelas o grupos grandes, contáctanos directo.",
      pricing: [
        {
          name: "Admisión General",
          price: "$40",
          unit: "/ niño / sesión",
          desc: "Drop-in por sesión, mañana o tarde. Sin paquetes ni reservas largas.",
          featured: true,
        },
        {
          name: "Escuelas y Grupos Grandes",
          price: "Hablemos",
          unit: "Escríbenos o llámanos",
          desc: "Escuelas, campamentos y grupos de 8+ — contáctanos directo para precio personalizado y fechas.",
          contact: true,
        },
      ],
      faq_title: "Bueno saber",
      faq: [
        {
          q: "¿Hay edad mínima?",
          a: "No. El programa está diseñado para todas las edades — bebés con un adulto, niños en edad escolar y adolescentes son bienvenidos.",
        },
        {
          q: "¿Los padres deben quedarse?",
          a: "Aceptamos drop-off. Los padres también son bienvenidos a quedarse y participar.",
        },
        {
          q: "¿Por qué sin electrónicos?",
          a: "Protegemos la magia de una tarde desconectada — los niños reconectan con animales, naturaleza y entre ellos. Los celulares se guardan al ingresar.",
        },
        {
          q: "¿Es bilingüe?",
          a: "Sí. Todo nuestro equipo corre el programa en inglés y español.",
        },
        {
          q: "¿Podemos añadir cabalgata?",
          a: "Sí — pregúntanos por combinar la sesión de finca con nuestras cabalgatas en la playa para un día completo en Hacienda Carey.",
        },
      ],
      cta_band: {
        title: "Reserva tu espacio en la finca.",
        sub: "Las sesiones se llenan rápido entre semana. Elige una mañana o tarde que le sirva a la familia y reserva en menos de un minuto.",
        button: "Reservar una Sesión",
      },
    },
  },
};
